import { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HorizontalSectionProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  showViewAll?: boolean;
  onViewAll?: () => void;
  icon?: React.ReactNode;
}

export function HorizontalSection({
  title,
  subtitle,
  children,
  className,
  showViewAll = false,
  onViewAll,
  icon,
}: HorizontalSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        el.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    
    const scrollAmount = el.clientWidth * 0.75;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className={cn('relative', className)}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3 px-6">
        <div className="flex items-center gap-2.5">
          {icon && (
            <div className="p-1.5 rounded-lg bg-primary/10 text-primary">
              {icon}
            </div>
          )}
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-foreground">
              {title}
            </h2>
            {subtitle && (
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {showViewAll && onViewAll && (
            <button 
              onClick={onViewAll}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              View all
            </button>
          )}
          
          {/* Navigation arrows - always visible */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={cn(
                'h-9 w-9 rounded-full flex items-center justify-center transition-all',
                'bg-white/10 hover:bg-white/20 active:scale-95',
                'border border-white/20 shadow-lg',
                canScrollLeft 
                  ? 'opacity-100 text-white' 
                  : 'opacity-30 cursor-not-allowed text-white/50'
              )}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={cn(
                'h-9 w-9 rounded-full flex items-center justify-center transition-all',
                'bg-white/10 hover:bg-white/20 active:scale-95',
                'border border-white/20 shadow-lg',
                canScrollRight 
                  ? 'opacity-100 text-white' 
                  : 'opacity-30 cursor-not-allowed text-white/50'
              )}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="relative group">
        {/* Left fade gradient */}
        <div className={cn(
          'absolute left-0 top-0 bottom-0 w-16 z-10 pointer-events-none',
          'bg-gradient-to-r from-background to-transparent',
          'transition-opacity duration-300',
          canScrollLeft ? 'opacity-100' : 'opacity-0'
        )} />
        
        {/* Right fade gradient */}
        <div className={cn(
          'absolute right-0 top-0 bottom-0 w-16 z-10 pointer-events-none',
          'bg-gradient-to-l from-background to-transparent',
          'transition-opacity duration-300',
          canScrollRight ? 'opacity-100' : 'opacity-0'
        )} />

        {/* Scrollable container - touch-friendly with snap scrolling */}
        <div
          ref={scrollRef}
          className={cn(
            'flex gap-4 overflow-x-auto px-6 pb-2',
            'scrollbar-hide scroll-smooth',
            'snap-x snap-mandatory'
          )}
          style={{
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {children}
        </div>
      </div>
    </motion.section>
  );
}
